<template>
  <v-alert :title="t('components.companies.feeDestination.title')" icon="mdi-account-credit-card">
    <p class="d-flex flex-column flex-md-row ga-6 ga-md-0">
      {{
        t("components.companies.feeDestination.description", {
          name: merchantsStore.currentMerchant?.legalEntityName,
        })
      }}
    </p>
    <v-select
      v-model="selectedPaymentFeeDestinationBa"
      density="compact"
      :items="formattedFeeDestinations"
      item-title="text"
      item-value="value"
      class="mt-5"
      hide-details
      @update:model-value="openConfirmModal"
    />
  </v-alert>

  <v-dialog v-model="confirmDialog" max-width="500px">
    <v-card>
      <template #title>
        <h3 class="text-error">
          {{ t("components.companies.feeDestination.confirmTitle") }}
        </h3>
      </template>
      <v-card-text>
        {{
          t("components.companies.feeDestination.confirmDescription", {
            name: merchantsStore.currentMerchant?.legalEntityName,
            destination: selectLabels[selectedPaymentFeeDestinationBa],
          })
        }}
      </v-card-text>

      <v-card-text>
        <v-alert type="error" variant="outlined" icon="$warning">
          {{ t("components.companies.feeDestination.warning") }}
          <p class="font-weight-bold mt-5">
            {{ t("components.companies.feeDestination.configWarning") }}
          </p>
        </v-alert>
      </v-card-text>
      <template #actions>
        <v-btn-nd :text="t('form.cancel')" class="ms-4 my-4" @click="cancelChange" />
        <v-spacer />
        <v-btn
          color="error"
          class="bg-error on-error me-4 my-4"
          :text="t('form.confirm')"
          :loading="loading"
          @click="confirmChange"
        />
      </template>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { UpdateMerchantInternalSettingsRequestPaymentFeeDestinationBaEnum } from "@tableyeti/merchant-service";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useYetipayApi } from "@/composables/useYetipayApi";
import { useApplicationStore } from "@/store/application";
import { useMerchantsStore } from "@/store/merchants";

const { t } = useI18n();
const { api, currentMerchantId } = useYetipayApi();
const merchantsStore = useMerchantsStore();
const applicationStore = useApplicationStore();

const currentPaymentFeeDestinationBa =
  merchantsStore.currentMerchant?.settings?.paymentFeeDestinationBa ?? "liable-account-ba";

const selectLabels = {
  "liable-account-ba": t("components.companies.feeDestination.destinations.liableAccount"),
  "store-ba": t("components.companies.feeDestination.destinations.storeBa"),
  "account-holder-fees-ba": t("components.companies.feeDestination.destinations.accountHolderFees"),
};

const feeDestinations = Object.values(
  UpdateMerchantInternalSettingsRequestPaymentFeeDestinationBaEnum,
);
const formattedFeeDestinations = feeDestinations.map((value) => ({
  text: selectLabels[value],
  value,
}));

const selectedPaymentFeeDestinationBa =
  ref<UpdateMerchantInternalSettingsRequestPaymentFeeDestinationBaEnum>(
    currentPaymentFeeDestinationBa as UpdateMerchantInternalSettingsRequestPaymentFeeDestinationBaEnum,
  );
const previousPaymentFeeDestinationBa =
  ref<UpdateMerchantInternalSettingsRequestPaymentFeeDestinationBaEnum>(
    currentPaymentFeeDestinationBa as UpdateMerchantInternalSettingsRequestPaymentFeeDestinationBaEnum,
  );

const confirmDialog = ref(false);
const loading = ref(false);

const openConfirmModal = () => {
  confirmDialog.value = true;
};

const confirmChange = async () => {
  loading.value = true;
  try {
    await api.updateMerchantInternalSettings(currentMerchantId, {
      paymentFeeDestinationBa: selectedPaymentFeeDestinationBa.value,
    });
    await new Promise((resolve) => setTimeout(resolve, 1000));
    previousPaymentFeeDestinationBa.value = selectedPaymentFeeDestinationBa.value;
    applicationStore.notifyUser({
      message: t("components.companies.feeDestination.successMessage"),
      type: "success",
    });
  } catch (error) {
    console.error(error);
    applicationStore.notifyUser({
      message: t("components.companies.feeDestination.errorMessage"),
      type: "error",
    });
  } finally {
    loading.value = false;
    confirmDialog.value = false;
  }
};

const cancelChange = () => {
  selectedPaymentFeeDestinationBa.value = previousPaymentFeeDestinationBa.value;
  confirmDialog.value = false;
};
</script>
